import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ItemReviewStatus } from "@forgepoint/shared";

/**
 * Props for the ReviewStatusIndicator component.
 * Renders a colored dot indicating item review status.
 */
interface ReviewStatusIndicatorProps {
  status: ItemReviewStatus | null | undefined;
  onClick?: () => void;
  size?: "sm" | "md";
  /** When true, show green dot for REVIEWED (used in assembly tree). Default: false. */
  showReviewed?: boolean;
}

/**
 * Renders a yellow or green dot indicating whether an item needs review.
 * Yellow = agent-modified, needs review. Green = user-reviewed.
 */
export function ReviewStatusIndicator({
  status,
  onClick,
  size = "md",
  showReviewed = false,
}: ReviewStatusIndicatorProps) {
  if (!status) {
    return null;
  }

  if (status === "REVIEWED" && !showReviewed) {
    return null;
  }

  const isNeedsReview = status === "NEEDS_REVIEW";
  const sizeClasses = size === "sm" ? "h-2 w-2" : "h-2.5 w-2.5";

  const tooltipText = isNeedsReview
    ? onClick
      ? "Needs review — click to mark as reviewed"
      : "Needs review"
    : "Reviewed";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              if (onClick) {
                e.stopPropagation();
                onClick();
              }
            }}
            className={cn(
              "rounded-full flex-shrink-0",
              isNeedsReview ? "bg-amber-400" : "bg-green-500",
              isNeedsReview && "animate-pulse",
              sizeClasses,
              onClick &&
                "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-current",
              !onClick && "cursor-default",
            )}
            aria-label={tooltipText}
          />
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
